import React from 'react';
import { Card, Descriptions, Spin, Empty, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getAssetById } from '@/api/assets';
import { useAuth } from '@/contexts/AuthContext';
import dayjs from 'dayjs';


interface AssetTabProps {
  assetId: string;
}

const AssetTab: React.FC<AssetTabProps> = ({ assetId }) => {
  const { t } = useTranslation(['assets', 'common']);
  const { firestoreUser } = useAuth();

  const { data: asset, isLoading, error } = useQuery({
    queryKey: ['asset', assetId],
    queryFn: () => getAssetById(assetId, firestoreUser!),
    enabled: !!assetId && !!firestoreUser,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <Card>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (error || !asset) {
    return (
      <Card>
        <Empty description={t('assets:notFound')} />
      </Card>
    );
  }

  return (
    <Card>
      <Descriptions bordered column={1}>
        <Descriptions.Item label={t('assets:fields.name')}>
          {asset.name}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('assets:fields.category')}>
          <Tag color="blue">{t(`assets:categories.${asset.category}`)}</Tag>
        </Descriptions.Item>
        
        {asset.subCategory && (
          <Descriptions.Item label={t('assets:fields.subCategory')}>
            {asset.subCategory}
          </Descriptions.Item>
        )}
        
        <Descriptions.Item label={t('assets:fields.status')}>
          <Tag color={
            asset.status === 'new' || asset.status === 'good' ? 'green' :
            asset.status === 'fair' ? 'orange' :
            asset.status === 'poor' || asset.status === 'damaged' ? 'red' :
            asset.status === 'maintenance' ? 'blue' :
            asset.status === 'retired' || asset.status === 'lost' ? 'default' : 'green'
          }>
            {t(`assets:status.${asset.status}`)}
          </Tag>
        </Descriptions.Item>
        
        {asset.location && (
          <Descriptions.Item label={t('assets:fields.location')}>
            {asset.location}
          </Descriptions.Item>
        )}
        
        {asset.purchaseDate && (
          <Descriptions.Item label={t('assets:fields.purchaseDate')}>
            {dayjs(asset.purchaseDate).format('DD/MM/YYYY')}
          </Descriptions.Item>
        )}
        
        {asset.purchasePrice && (
          <Descriptions.Item label={t('assets:fields.purchasePrice')}>
            {asset.purchasePrice.amount} {asset.purchasePrice.currency}
          </Descriptions.Item>
        )}
        
        {asset.block && (
          <Descriptions.Item label={t('assets:fields.block')}>
            {asset.block.name}
          </Descriptions.Item>
        )}
        
        {asset.floor && (
          <Descriptions.Item label={t('assets:fields.floor')}>
            {asset.floor.name}
          </Descriptions.Item>
        )}
        
        {asset.amenity && (
          <Descriptions.Item label={t('assets:fields.amenity')}>
            {asset.amenity.name}
          </Descriptions.Item>
        )}
        
        {asset.employee && (
          <Descriptions.Item label={t('assets:fields.employee')}>
            {asset.employee.name}
          </Descriptions.Item>
        )}
        
        {asset.vendor && (
          <Descriptions.Item label={t('assets:fields.vendor')}>
            {asset.vendor.name}
          </Descriptions.Item>
        )}
        
        {asset.warranty && (
          <Descriptions.Item label={t('assets:fields.warranty')}>
            <div>
              <div>{t('assets:fields.warrantyProvider')}: {asset.warranty.provider}</div>
              <div>{t('assets:fields.warrantyStart')}: {dayjs(asset.warranty.startDate).format('DD/MM/YYYY')}</div>
              <div>{t('assets:fields.warrantyEnd')}: {dayjs(asset.warranty.endDate).format('DD/MM/YYYY')}</div>
              <div>{t('assets:fields.warrantyDetails')}: {asset.warranty.details}</div>
            </div>
          </Descriptions.Item>
        )}
        
        {asset.specifications && (
          <Descriptions.Item label={t('assets:fields.specifications')}>
            <div>
              {asset.specifications.brand && <div>{t('assets:fields.brand')}: {asset.specifications.brand}</div>}
              {asset.specifications.model && <div>{t('assets:fields.model')}: {asset.specifications.model}</div>}
              {asset.specifications.serialNumber && <div>{t('assets:fields.serialNumber')}: {asset.specifications.serialNumber}</div>}
              {asset.specifications.dimensions && <div>{t('assets:fields.dimensions')}: {asset.specifications.dimensions}</div>}
              {asset.specifications.weight && <div>{t('assets:fields.weight')}: {asset.specifications.weight} kg</div>}
              {asset.specifications.color && <div>{t('assets:fields.color')}: {asset.specifications.color}</div>}
            </div>
          </Descriptions.Item>
        )}
        
        <Descriptions.Item label={t('assets:fields.documents')}>
          {asset.documents?.length ? `${asset.documents.length} document(s)` : '-'}
        </Descriptions.Item>
        
        {asset.notes && (
          <Descriptions.Item label={t('assets:fields.notes')}>
            {asset.notes}
          </Descriptions.Item>
        )}
      </Descriptions>
    </Card>
  );
};

export default AssetTab; 